/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import { TooltipModel } from 'chart.js'
import { CourseName } from 'theme/utils'
import { ReactComponent as TooltipTriangle } from 'assets/icons/tooltip-triangle.svg'
import * as classes from './styles'

export type ProgressPointData = {
  dateRaw: Date
  xAxisValue: string
  yAxisValue: number
  name: string
  rawScore: number
  totalRawScore: number
  standardScore: number
}

export type Position = {
  top: number
  left: number
  displayToTheLeft: boolean
}

export type Props<T> = {
  isVisible: boolean
  subject: CourseName
  tooltipData: TooltipModel<'line'> | null
  data?: T[]
  position: Position | null
  className?: string
  isPastPracticeTest?: boolean
  hideTooltipTriangle?: boolean
  renderTooltipContent: (pointData: T) => React.ReactNode
}

export const progressPointDataGuard = (data: unknown): data is ProgressPointData =>
  typeof data === 'object' && data !== null && 'name' in data

export const ChartTooltip = <T,>({
  isVisible,
  subject,
  tooltipData,
  data,
  position,
  className,
  hideTooltipTriangle = false,
  renderTooltipContent,
}: Props<T>) => {
  const dataIndex = tooltipData?.dataPoints?.[0]?.dataIndex
  const pointData = (dataIndex ? data?.[dataIndex] : tooltipData?.dataPoints?.[0]?.raw) as T | undefined

  return (
    <div
      css={classes.chartTooltip(isVisible, subject, !!position?.displayToTheLeft)}
      className={className}
      style={{
        top: position?.top,
        left: position?.left,
      }}
    >
      {!hideTooltipTriangle && <TooltipTriangle css={classes.tooltipTriangle(subject, !!position?.displayToTheLeft)} />}
      {pointData ? (
        renderTooltipContent(pointData)
      ) : (
        <Typography css={classes.title}>Nothing found for this point</Typography>
      )}
    </div>
  )
}
